import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { ChevronLeft, ChevronRight, PlugZap } from "lucide-react"
import Animate, { AnimationTypes } from "../Animate/Animate"
import { LandingIntegrationEntity } from "../../Objects/Landing/LandingIntegrationEntity"
import { useGetLandingIntegrations } from "../../hooks/Landing/useGetLandingIntegrations"

const fallbackLogo =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='120' height='120' viewBox='0 0 120 120'%3E%3Crect width='120' height='120' rx='24' fill='%23fff2f2'/%3E%3Cpath d='M60 30v18m0 24v18M42 42l13 13m10 10 13 13M30 60h18m24 0h18M42 78l13-13m10-10 13-13' stroke='%23ef4444' stroke-width='6' stroke-linecap='round'/%3E%3C/svg%3E"

const IntegrationsCarousel: React.FC = () => {
  const { t } = useTranslation("landpage")

  const { handleGetLandingIntegrations } = useGetLandingIntegrations()

  const [integrations, setIntegrations] = useState<LandingIntegrationEntity[]>([])

  const [isLoading, setIsLoading] = useState(true)

  const [activeIndex, setActiveIndex] = useState(0)

  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const loadIntegrations = async () => {
      setIsLoading(true)

      try {
        const items = await handleGetLandingIntegrations()

        setIntegrations(items)
      } catch {
        setIntegrations([])
      } finally {
        setIsLoading(false)
      }
    }

    loadIntegrations()
  }, [handleGetLandingIntegrations])

  const functionalCount = useMemo(() => integrations.filter((item) => !item.isInDevelopment).length, [integrations])

  const developmentCount = useMemo(() => integrations.filter((item) => item.isInDevelopment).length, [integrations])

  const scrollToIndex = (index: number) => {
    const container = containerRef.current

    if (!container) {
      return
    }

    const safeIndex = Math.max(0, Math.min(index, integrations.length - 1))

    const target = container.children[safeIndex] as HTMLElement | undefined

    if (!target) {
      return
    }

    target.scrollIntoView({ behavior: "smooth", inline: "start", block: "nearest" })
    setActiveIndex(safeIndex)
  }

  const onNext = () => scrollToIndex((activeIndex + 1) % Math.max(integrations.length, 1))
  const onPrevious = () => scrollToIndex((activeIndex - 1 + integrations.length) % Math.max(integrations.length, 1))

  if (isLoading) {
    return (
      <section className="px-6 py-8 lg:py-10">
        <div className="mx-auto max-w-[1280px] rounded-xl  bg-gradient-to-br from-white via-orange-50 to-amber-50 p-6 shadow-xl">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="animate-pulse rounded-xl border border-orange-100 bg-white p-4">
                <div className="h-14 w-14 rounded-xl bg-orange-100" />
                <div className="mt-4 h-3 w-20 rounded bg-orange-100" />
                <div className="mt-2 h-5 w-2/3 rounded bg-amber-100" />
                <div className="mt-2 h-4 w-full rounded bg-orange-100" />
              </div>
            ))}
          </div>
        </div>
      </section>
    )
  }

  if (integrations.length === 0) {
    return null
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
      <section id="integrations" className="py-2 lg:py-10">
        <div className="mx-auto max-w-[1280px] overflow-hidden rounded-2xl shadow-sm">
          <div className="px-5 py-6 md:px-8 md:py-8 lg:px-10 lg:py-10">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-[0.7rem] font-extrabold uppercase tracking-[0.2em] text-orange-700">
                  <PlugZap size={14} /> {t("integrations.badge")}
                </span>
              </div>

              <div className="flex flex-wrap gap-2">
                <span className="rounded-full border border-emerald-400 bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                  {t("integrations.functional_count", { count: functionalCount })}
                </span>

                <span className="rounded-full border border-orange-400 bg-orange-50 px-3 py-1 text-xs font-black text-orange-700">
                  {t("integrations.development_count", { count: developmentCount })}
                </span>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={onPrevious}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-700 transition hover:scale-105"
                aria-label={t("integrations.previous")}
                data-interaction-description-key="interactionDescriptions:components.landPage.integrationsCarousel.button.previous"
              >
                <ChevronLeft size={20} />
              </button>

              <button
                type="button"
                onClick={onNext}
                className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-700 transition hover:scale-105"
                aria-label={t("integrations.next")}
                data-interaction-description-key="interactionDescriptions:components.landPage.integrationsCarousel.button.next"
              >
                <ChevronRight size={20} />
              </button>
            </div>

            <div
              ref={containerRef}
              className="mt-4 flex gap-4 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory"
            >
              {integrations.map((integration, index) => (
                <article
                  key={`${integration.name}-${index}`}
                  className="min-w-[85%] max-w-[85%] snap-start rounded-xl bg-gradient-to-br from-white to-orange-50 p-4 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-w-[340px] sm:max-w-[340px]"
                >
                  <div className="flex items-start justify-between gap-3">
                    <img
                      src={integration.logoUrl || fallbackLogo}
                      alt={integration.name}
                      loading="lazy"
                      className="h-14 w-14 rounded-xl border border-orange-100 bg-white object-contain p-2"
                      onError={(event) => {
                        event.currentTarget.src = fallbackLogo
                      }}
                    />

                    <span
                      className={`rounded-full px-2.5 py-1 text-[0.65rem] font-black uppercase tracking-wide ${
                        integration.isInDevelopment
                          ? "border border-orange-200 bg-orange-50 text-orange-700"
                          : "border border-emerald-200 bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {integration.isInDevelopment ? t("integrations.status_future") : t("integrations.status_active")}
                    </span>
                  </div>

                  <p className="mt-4 text-[0.65rem] font-black uppercase tracking-[0.14em] text-orange-500">
                    {integration.category || t("integrations.default_category")}
                  </p>

                  <h3 className="mt-2 text-lg font-black text-slate-900">{integration.name}</h3>

                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {integration.description || t("integrations.default_description")}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>
    </Animate>
  )
}

export default IntegrationsCarousel
